import { useCallback, useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { Chart as ChartJS } from "chart.js";
import { Bar } from "react-chartjs-2";
import { TbCoin } from "react-icons/tb";
import { useBaasInstance } from "../../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../../Hooks/useBaasCapabilities";
import { useColorMode } from "../../../../../Hooks/useColorMode";
import { fetchBaasCardPrograms, fetchBaasPeerCardPrograms } from "../../../../../Services/baasCardProgramsApi";
import { formatMonetaryValue } from "../../../../../Utils/formatters";
import { shouldShowBaasCustodyAggregate } from "../../baasInstanceListFilter";
import {
  aggregateBaasProgramsCreditMetrics,
  EMPTY_BAAS_HOME_CREDIT_METRICS,
} from "../baasHomeMetrics";
import {
  buildBaasHomeStackedBarOptions,
  registerBaasHomeBarChart,
} from "./BaasHomeChartOptions";
import { BaasHomeSnapshotSection } from "./BaasHomeSnapshotSection";

registerBaasHomeBarChart(ChartJS);

const formatPercent = (value, fractionDigits = 0) =>
  `${new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value)}%`;

const DisabledCreditPanel = ({ message }) => (
  <div className="app-card flex h-full min-h-[12rem] flex-1 flex-col items-center justify-center gap-3 border border-dashed border-gray-200 bg-gray-50/80 p-6 text-center opacity-80 dark:border-brand-dark-border dark:bg-brand-dark-surface/60">
    <div className="summary-card-icon opacity-60">
      <TbCoin className="h-5 w-5 text-gray-400" aria-hidden />
    </div>
    <p className="max-w-xs text-sm text-gray-500 dark:text-gray-400">{message}</p>
  </div>
);

export const BaasHomeCreditoPanel = ({
  displayCurrency = "BRL",
  /** Nested under Emissor — show subsection label, no peer “Ver crédito”. */
  embedded = false,
}) => {
  const { selectedInstance, listInstanceFilter } = useBaasInstance();
  const { can, getBlockReason, loading: capabilitiesLoading } = useBaasCapabilities();
  const { isDark } = useColorMode();
  const [programs, setPrograms] = useState([]);
  const [programsLoading, setProgramsLoading] = useState(false);

  const creditAllowed = !capabilitiesLoading && can("credit.panel.view");
  const creditBlockReason = getBlockReason("credit.panel.view");
  const disabledMessage = capabilitiesLoading
    ? "Carregando…"
    : creditBlockReason === "product_disabled"
      ? intl.get("baas.home.capabilities.issuerDisabled")
      : intl.get("baas.home.capabilities.permissionDenied");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!selectedInstance || !creditAllowed) {
        setPrograms([]);
        setProgramsLoading(false);
        return;
      }

      setProgramsLoading(true);
      try {
        const [own, peers] = await Promise.all([
          fetchBaasCardPrograms(selectedInstance),
          shouldShowBaasCustodyAggregate(selectedInstance, listInstanceFilter)
            ? fetchBaasPeerCardPrograms(selectedInstance)
            : Promise.resolve([]),
        ]);
        if (!cancelled) {
          const byId = new Map();
          for (const row of own) {
            if (!row?.id) continue;
            byId.set(row.id, row);
          }
          for (const row of peers) {
            if (!row?.id) continue;
            byId.set(row.id, row);
          }
          setPrograms([...byId.values()]);
        }
      } catch {
        if (!cancelled) setPrograms([]);
      } finally {
        if (!cancelled) setProgramsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [creditAllowed, selectedInstance, listInstanceFilter]);

  const creditMetrics = useMemo(
    () =>
      programsLoading
        ? { ...EMPTY_BAAS_HOME_CREDIT_METRICS, currency: displayCurrency }
        : aggregateBaasProgramsCreditMetrics(programs, displayCurrency),
    [displayCurrency, programs, programsLoading],
  );
  const currency = creditMetrics.currency || "BRL";

  const formatTooltipValue = useCallback(
    (value) => formatMonetaryValue(value, currency, { maximumFractionDigits: 0 }),
    [currency],
  );

  const chartOptions = useMemo(
    () => buildBaasHomeStackedBarOptions(isDark, formatTooltipValue),
    [isDark, formatTooltipValue],
  );

  const chartData = useMemo(
    () => ({
      labels: [""],
      datasets: [
        {
          label: intl.get("baas.home.dashboard.creditChart.legendCollateralized"),
          data: [creditMetrics.runningCollateralizedBrl],
          backgroundColor: "#10b981",
          borderRadius: 4,
        },
        {
          label: intl.get("baas.home.dashboard.creditChart.legendUnsecured"),
          data: [creditMetrics.runningUnsecuredBrl],
          backgroundColor: "#e7a73f",
          borderRadius: 4,
        },
        {
          label: intl.get("baas.home.dashboard.creditChart.legendAvailable"),
          data: [creditMetrics.availableLimitBrl],
          backgroundColor: isDark ? "rgba(255,255,255,0.12)" : "#e5e7eb",
          borderRadius: 4,
        },
      ],
    }),
    [creditMetrics, isDark],
  );

  const formatMoney = useCallback(
    (value) =>
      formatMonetaryValue(value, currency, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [currency],
  );

  const statItems = useMemo(
    () => [
      {
        key: "collateral",
        label: intl.get("baas.home.dashboard.creditChart.collateralDeposited"),
        value: formatMoney(creditMetrics.collateralDepositedBrl),
      },
      {
        key: "granted",
        label: intl.get("baas.home.unsecuredCredit.title"),
        value: formatMoney(creditMetrics.unsecuredGrantedBrl),
      },
      {
        key: "exposure",
        label: intl.get("baas.home.netExposure.title"),
        value: formatMoney(creditMetrics.netExposureBrl),
      },
    ],
    [creditMetrics, formatMoney],
  );

  return (
    <BaasHomeSnapshotSection
      embedded={embedded}
      title={intl.get("baas.home.creditSection")}
      viewTo={
        embedded || !creditAllowed ? null : "/baas/cartoes#baas-credito"
      }
      viewLabel={intl.get("baas.home.dashboard.viewCredito")}
    >
      {!creditAllowed ? (
        <DisabledCreditPanel message={disabledMessage} />
      ) : (
        <div className="app-card flex h-full min-h-0 flex-1 flex-col overflow-hidden">
          <div className="border-b border-gray-100 px-4 py-4 dark:border-brand-dark-border sm:px-5">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {intl.get("baas.home.runningCredit.title")}
            </p>
            <div className="mt-1 flex flex-wrap items-end gap-3">
              <p className="text-2xl font-bold text-brand-dark dark:text-gray-100 sm:text-3xl">
                {formatMoney(creditMetrics.runningCreditBrl)}
              </p>
              <span className="mb-1 inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700 dark:bg-brand-dark-border dark:text-gray-200">
                {formatPercent(creditMetrics.utilizationRate)}{" "}
                {intl.get("baas.home.utilization.title").toLowerCase()}
              </span>
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {intl.get("baas.home.dashboard.creditChart.limitHint", {
                amount: formatMoney(creditMetrics.totalLimitBrl),
              })}
            </p>
          </div>

          <div className="flex min-h-0 flex-1 flex-col px-4 py-4 sm:px-5">
            <div className="flex min-h-0 flex-1 flex-col justify-center">
              <p className="mb-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                {intl.get("baas.home.dashboard.creditChart.title")}
              </p>
              <div className="h-8 w-full">
                <Bar data={chartData} options={chartOptions} />
              </div>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-xs text-gray-500 dark:text-gray-400">
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  {intl.get("baas.home.dashboard.creditChart.legendCollateralized")}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-[#e7a73f]" />
                  {intl.get("baas.home.dashboard.creditChart.legendUnsecured")}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className={`h-2 w-2 rounded-full ${isDark ? "bg-white/20" : "bg-gray-200"}`}
                  />
                  {intl.get("baas.home.dashboard.creditChart.legendAvailable")}
                </span>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3 border-t border-gray-100 pt-4 dark:border-brand-dark-border sm:grid-cols-3">
              {statItems.map((item) => (
                <div key={item.key} className="min-w-0">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{item.label}</p>
                  <p className="mt-1 truncate text-sm font-semibold text-brand-dark dark:text-gray-100">
                    {item.value}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </BaasHomeSnapshotSection>
  );
};
